import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import WebScrapingConfigForm from './WebScrapingConfigForm';
import ModalActionButtons from '../modal/ModalActionButtons';
import {
  scrapeWebsite,
  getScrapingJobStatus,
  getScrapingJobResult
} from '../../api/prospects';
import {
  AccountProspectResponse,
  AccountProspectScrapeQuery
} from '../../types/prospects';
import { useSnackbar } from '../../context/SnackbarContext';
export interface ScrapeWebsiteInputParamModalContentRef {
  triggerScrape: () => void;
  canSubmit: boolean;
  scraping: boolean;
}

// NOTE: Can rename this to SearchAccountProspectsByFunding
interface ScrapeWebsiteInputParamModalContentProps {
  onConfirm: (
    rows: AccountProspectResponse[],
    config?: Record<string, any>
  ) => void;
  onCancel: () => void;
  /** When true, show Cancel instead of Back */
  hideCancel?: boolean;
  /** When true, hide action buttons (parent will render them in footer) */
  hideButtons?: boolean;
  /** Called when canSubmit or scraping state changes (for parent to control footer buttons) */
  onStateChange?: (state: { canSubmit: boolean; scraping: boolean }) => void;
}

const ScrapeWebsiteInputParamModalContent = forwardRef<
  ScrapeWebsiteInputParamModalContentRef,
  ScrapeWebsiteInputParamModalContentProps
>(
  (
    {
      onConfirm,
      onCancel,
      hideCancel = false,
      hideButtons = false,
      onStateChange
    },
    ref
  ) => {
    const { showSnackbar } = useSnackbar();

    const [websiteUrl, setWebsiteUrl] = useState<string>('');
    const [scrapeInstructions, setScrapeInstructions] = useState<string>('');
    const [scraping, setScraping] = useState<boolean>(false);
    const [pollingInterval, setPollingInterval] =
      useState<NodeJS.Timeout | null>(null);
    const [scrapeConfig, setScrapeConfig] = useState<Record<
      string,
      any
    > | null>(null);
    const scrapeConfigRef = useRef<Record<string, any> | null>(null);

    const canSubmit = Boolean(
      websiteUrl.trim() && scrapeInstructions.trim() && !scraping
    );

    useEffect(() => {
      onStateChange?.({ canSubmit, scraping });
    }, [canSubmit, scraping, onStateChange]);

    // Cleanup polling interval on unmount
    useEffect(() => {
      return () => {
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
      };
    }, [pollingInterval]);

    const pollJobStatus = async (jobId: string, intervalId: NodeJS.Timeout) => {
      try {
        const status = await getScrapingJobStatus(jobId);

        if (status.status === 'finished') {
          // Job completed, get results
          clearInterval(intervalId);
          setPollingInterval(null);

          const results = await getScrapingJobResult(jobId);
          if (!results || results.length === 0) {
            showSnackbar('No prospects found under search criteria', 'error');
            setScraping(false);
            return;
          }

          // Pass the config along with results - use ref to ensure we have the latest value
          const configToPass = scrapeConfigRef.current || scrapeConfig;
          onConfirm(results, configToPass || undefined);
          setScraping(false);
          setScrapeConfig(null);
          scrapeConfigRef.current = null;
        } else if (status.status === 'failed') {
          // Job failed
          clearInterval(intervalId);
          setPollingInterval(null);
          showSnackbar(
            `Scraping failed: ${status.error || 'Unknown error'}`,
            'error'
          );
          setScraping(false);
        }
        // If status is 'pending' or 'processing', continue polling
      } catch (error) {
        clearInterval(intervalId);
        setPollingInterval(null);
        showSnackbar('Failed to check job status', 'error');
        setScraping(false);
      }
    };

    const handleScrapeWebsite = async () => {
      setScraping(true);

      try {
        const query: AccountProspectScrapeQuery = {
          website_url: websiteUrl.trim(),
          prompt: scrapeInstructions.trim()
        };

        // Store config to pass to onConfirm later (both in state and ref)
        const config = {
          website_url: websiteUrl.trim(),
          prompt: scrapeInstructions.trim()
        };
        setScrapeConfig(config);
        scrapeConfigRef.current = config;

        const jobResponse = await scrapeWebsite(query);

        // Start polling for job status
        const interval: NodeJS.Timeout = setInterval(
          () => pollJobStatus(jobResponse.job_id, interval),
          5000
        ); // Poll every 5 seconds
        setPollingInterval(interval);
      } catch {
        showSnackbar('Failed to create scraping job', 'error');
        setScraping(false);
        setScrapeConfig(null);
        scrapeConfigRef.current = null;
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        triggerScrape: handleScrapeWebsite,
        canSubmit,
        scraping
      }),
      [canSubmit, scraping, handleScrapeWebsite]
    );

    if (scraping) {
      return (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
            sx={{ width: '100%', height: '100%', flexDirection: 'column' }}
          >
            <CircularProgress
              sx={(theme) => ({
                color: theme.customColors.themePrimary
              })}
            />
            <Typography variant="body1">
              Scraping website... This may take a few minutes.
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <WebScrapingConfigForm
          websiteUrl={websiteUrl}
          targetProperties={scrapeInstructions}
          onWebsiteUrlChange={setWebsiteUrl}
          onTargetPropertiesChange={setScrapeInstructions}
          disabled={scraping}
        />

        {!hideButtons && (
          <ModalActionButtons
            asDialogActions={false}
            secondaryLabel={hideCancel ? 'Cancel' : 'Back'}
            primaryLabel={scraping ? 'Scraping…' : 'Start Scraping'}
            onSecondary={onCancel}
            onPrimary={handleScrapeWebsite}
            primaryDisabled={!canSubmit}
            secondaryDisabled={scraping}
          />
        )}
      </Box>
    );
  }
);

ScrapeWebsiteInputParamModalContent.displayName =
  'ScrapeWebsiteInputParamModalContent';

export default ScrapeWebsiteInputParamModalContent;
